import { type ISpot, SpotEventTypeEnum, SpotStatus } from './yard';

export const SPOT_STATUSES_BLOCKED_FOR_NEW_ASSIGNMENT: readonly SpotStatus[] = Object.freeze([
  SpotStatus.CLOSED,
  SpotStatus.LOCKED,
  SpotStatus.RESERVED_MANUAL,
  SpotStatus.RESERVED_AUTOMATED,
  SpotStatus.RESERVED
]) as readonly SpotStatus[];

export function isSpotStatusLegacyActive(status: SpotStatus): boolean {
  return !SPOT_STATUSES_BLOCKED_FOR_NEW_ASSIGNMENT.includes(status);
}

export function canSpotAcceptNewAssignment(spot: Pick<ISpot, 'isActive' | 'status'>): boolean {
  if (spot.status && spot.status !== SpotStatus.UNKNOWN) {
    return isSpotStatusLegacyActive(spot.status);
  }

  return spot.isActive !== false;
}

export type SpotStatusChangeRules = Record<SpotStatus, SpotStatus[]>;

export function getSpotStatusChangeRules(): SpotStatusChangeRules {
  return {
    [SpotStatus.OPEN]: [
      SpotStatus.CLOSED,
      SpotStatus.LOCKED,
      SpotStatus.ASSIGNED,
      SpotStatus.RESERVED_MANUAL,
      SpotStatus.RESERVED_AUTOMATED
    ],
    [SpotStatus.CLOSED]: [SpotStatus.OPEN, SpotStatus.ASSIGNED],
    [SpotStatus.LOCKED]: [SpotStatus.OPEN, SpotStatus.ASSIGNED],
    [SpotStatus.ASSIGNED]: [SpotStatus.OPEN, SpotStatus.CLOSED, SpotStatus.LOCKED],
    [SpotStatus.RESERVED_MANUAL]: [SpotStatus.OPEN, SpotStatus.ASSIGNED],
    [SpotStatus.RESERVED_AUTOMATED]: [SpotStatus.OPEN, SpotStatus.ASSIGNED],
    [SpotStatus.UNKNOWN]: [
      SpotStatus.OPEN,
      SpotStatus.CLOSED,
      SpotStatus.LOCKED,
      SpotStatus.ASSIGNED,
      SpotStatus.RESERVED_MANUAL,
      SpotStatus.RESERVED_AUTOMATED
    ],
    [SpotStatus.RESERVED]: [SpotStatus.OPEN, SpotStatus.ASSIGNED]
  };
}

export function isSpotStatusTransitionAllowed(from: SpotStatus, to: SpotStatus): boolean {
  if (from === to) {
    return true;
  }
  return getSpotStatusChangeRules()[from]?.includes(to) ?? false;
}

export function getSpotTransitionEvent(from: SpotStatus, to: SpotStatus): SpotEventTypeEnum {
  switch (to) {
    case SpotStatus.CLOSED:
    case SpotStatus.LOCKED:
      return SpotEventTypeEnum.CLOSE;
    case SpotStatus.RESERVED_MANUAL:
    case SpotStatus.RESERVED_AUTOMATED:
    case SpotStatus.RESERVED:
      return SpotEventTypeEnum.RESERVE;
    case SpotStatus.ASSIGNED:
      return SpotEventTypeEnum.ASSIGNMENT;
    case SpotStatus.OPEN:
      if (
        from === SpotStatus.RESERVED_MANUAL ||
        from === SpotStatus.RESERVED_AUTOMATED ||
        from === SpotStatus.RESERVED
      ) {
        return SpotEventTypeEnum.RELEASE_RESERVE;
      }
      if (from === SpotStatus.ASSIGNED) {
        return SpotEventTypeEnum.RELEASE;
      }
      return SpotEventTypeEnum.OPEN;
    default:
      return SpotEventTypeEnum.OTHER;
  }
}
